import { DropdownOption } from '@/common/getDropdownList';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import Select from '@/components/dataTable/TableSelect';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useDynamicLocale } from '@/hoslog-intl';
import { formatDate } from '@/lib/dateFormatter';
import { exportCsvSingleFilePapa } from '@/lib/exportCsv';
import { parseUserAgent } from '@/lib/parseUserAgent';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { ActivityLog } from '@/services/dashboard/conversionAnalytics/conversionAnalytics.types';
import * as React from 'react';
import { getColumns } from './Column';

interface ActivityLogTableProps {
  data: ActivityLog[];
  isLoading: boolean;
  pagination: Pagination;
}

export function ActivityLogTable({ data, isLoading, pagination }: ActivityLogTableProps) {
  const { t } = useDynamicLocale();
  const columns = React.useMemo(() => getColumns(t), [t]);
  const pageCount = Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize || 1));

  const ACTION_TYPES: DropdownOption[] = [
    { id: 'all', value: 'all', label: t('common.form.label.all') },
    { id: 'reservation', value: 'reservation', label: t('dashboard.conversion.page.reservation') },
    { id: 'phone', value: 'phone', label: t('dashboard.conversion.page.phone') },
  ];

  const TARGET_TYPES: DropdownOption[] = [
    { id: 'all', value: 'all', label: t('common.form.label.all') },
    { id: 'storePage', value: 'store_detail', label: t('dashboard.conversion.page.store') },
    { id: 'hostDetailPage', value: 'host_detail', label: t('dashboard.conversion.page.host') },
  ];

  const USER_STATUSES: DropdownOption[] = [
    { id: 'all', value: 'all', label: t('common.form.label.all') },
    { id: 'member', value: 'authenticated', label: t('dashboard.conversion.page.member') },
    { id: 'guest', value: 'guest', label: t('dashboard.conversion.page.guest') },
  ];

  const handleExport = () => {
    exportCsvSingleFilePapa(
      data.map((item, index) => {
        const { name, version, platform } = parseUserAgent(item.userAgent);
        const isStorePage = item.sourcePage === 'Store Page';
        const sourcePage = isStorePage
          ? t('dashboard.conversion.page.storePage')
          : item.sourcePage === 'Host Detail Page'
            ? t('dashboard.conversion.page.hostPage')
            : item.sourcePage;
        const actionType =
          item.actionType.toLowerCase() === 'phone'
            ? t('dashboard.conversion.page.phone')
            : t('dashboard.conversion.page.reservation');
        const userStatus =
          item.userStatus.toLowerCase() === 'guest'
            ? t('dashboard.conversion.page.guest')
            : item.userStatus.toLowerCase() === 'member'
              ? t('dashboard.conversion.page.member')
              : item.userStatus;
        const sourceCode = isStorePage ? item.clubCode : item.hostCode;

        return {
          SN: Math.max((pagination.currentPage ?? 1) - 1, 0) * (pagination.pageSize ?? 10) + index + 1,
          ActionType: actionType,
          SourcePage: sourceCode ? `${sourcePage}, ${sourceCode}` : sourcePage,
          TargetName: item.prefecture ? `${item.targetName}, ${item.prefecture}` : item.targetName,
          UserStatus: `${userStatus}, ${item.activityId}`,
          City: item.customerLocationJson.cityName ?? '',
          Browser: `Name = ${name}, Version = ${version}, Platform = ${platform}`,
          DateTime: formatDate(Number(item.dateTime), 'YYYY/MM/DD HH:mm'),
        };
      }),
      { fileName: 'activity-log', format: 'YYYY/MM/DD HH:mm' },
    );
  };

  const {
    table,
    actionTypeFilter,
    sourcePageTypeFilter,
    userStatusFilter,
    onActionTypeFilterChange,
    onSourcePageTypeFilterChange,
    onUserStatusFilterChange,
  } = useDataTable({
    data,
    columns,
    pageCount,
    meta: {
      defaultDatePreset: 'today',
      title: t('dashboard.conversion.tabs.activityLog'),
      searchPlaceholder: t('dashboard.conversion.page.searchByStoreCast'),
      totalPage: pagination.totalItems,
      loading: isLoading,
      actionKey: 'exportCsv',
      action: handleExport,
      actionDisabled: data.length === 0,
      selectClassName: 'justify-start',
    },
    getRowId: (row) => row.activityId,
    shallow: false,
    clearOnDefault: true,
  });
  const hasActiveFilters = actionTypeFilter !== 'all' || sourcePageTypeFilter !== 'all' || userStatusFilter !== 'all';
  const resetFilters = () => {
    onActionTypeFilterChange('all');
    onSourcePageTypeFilterChange('all');
    onUserStatusFilterChange('all');
  };

  return (
    <DataTable table={table}>
      <DataTableToolbar
        defaultDatePreset="today"
        hideViewOptions
        customFiltersActive={hasActiveFilters}
        onResetCustomFilters={resetFilters}
        buttonText="common.page.table.exportCSVText"
        filterChildren={
          <>
            <Select
              label={t('dashboard.conversion.page.actionType')}
              options={ACTION_TYPES}
              value={actionTypeFilter}
              onValueChange={onActionTypeFilterChange}
              hideDefaultOption
            />
            <Select
              label={t('dashboard.conversion.page.targetType')}
              options={TARGET_TYPES}
              value={sourcePageTypeFilter}
              onValueChange={onSourcePageTypeFilterChange}
              hideDefaultOption
              className="min-w-[180px]"
            />
            <Select
              label={t('dashboard.conversion.page.userStatus')}
              options={USER_STATUSES}
              value={userStatusFilter}
              onValueChange={onUserStatusFilterChange}
              hideDefaultOption
            />
          </>
        }
      />
    </DataTable>
  );
}
